import React from "react";
import styled, { keyframes } from "styled-components";
import { SCREEN_SM } from "sketon/ui/theme";

/*
 * Placeholder that mirrors the real book list geometry 1:1, so swapping it
 * for the loaded rows does not move anything on screen. Every dimension
 * below is copied from the matching rule in pages/main/index.tsx.
 */

const shimmer = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

const SkeletonBlock = styled.div`
  background-color: #EFE6D2;
  background-image: linear-gradient(
    90deg,
    rgba(248, 241, 225, 0) 0%,
    rgba(248, 241, 225, 0.9) 50%,
    rgba(248, 241, 225, 0) 100%
  );
  background-size: 200% 100%;
  background-repeat: no-repeat;
  animation: ${shimmer} 1.6s linear infinite;
  border-radius: 4px;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const StyledTagBarSkeleton = styled.div`
  width: 100%;
  display: flex;
  flex-flow: row;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;

  @media (max-width: ${SCREEN_SM}) {
    gap: 0.16rem;
    padding: 0 0.4rem;
    margin-bottom: 0.2rem;
    overflow: hidden;
  }
`;

const StyledChipSkeleton = styled(SkeletonBlock)<{ $w: number }>`
  width: ${(props) => props.$w}px;
  height: 32px;
  border-radius: 16px;
  flex-shrink: 0;

  @media (max-width: ${SCREEN_SM}) {
    width: ${(props) => props.$w / 50}rem;
    height: 0.56rem;
    border-radius: 0.28rem;
  }
`;

const StyledSectionHeaderSkeleton = styled.div`
  width: 100%;
  display: flex;
  flex-flow: row;
  justify-content: space-between;
  align-items: center;
  line-height: 50px;

  @media (max-width: ${SCREEN_SM}) {
    line-height: 0.6rem;
    padding: 0 0.4rem;
  }
`;

const StyledMonthSkeleton = styled(SkeletonBlock)`
  width: 150px;
  height: 28px;

  @media (max-width: ${SCREEN_SM}) {
    width: 2.9rem;
    height: 0.44rem;
  }
`;

const StyledNavSkeleton = styled(SkeletonBlock)`
  width: 64px;
  height: 24px;

  @media (max-width: ${SCREEN_SM}) {
    width: 1.2rem;
    height: 0.4rem;
  }
`;

/* Mirrors StyledListItemDiv */
const StyledListSkeleton = styled.div`
  width: 100%;
  padding: 0px 0px 20px;

  display: flex;
  flex-flow: column;
  align-items: stretch;

  background: #FFFFFF;
  border: 1px solid #EBD9B0;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(58, 46, 34, 0.06);

  margin-bottom: 30px;

  @media (max-width: ${SCREEN_SM}) {
    width: 100%;
    padding: 0.1rem 0.4rem;
    border-radius: 0px;
    margin-bottom: 10px;
  }
`;

/* Mirrors StyledBookItem */
const StyledItemSkeleton = styled.div`
  width: fit-content;
  padding: 20px 0px 0px 20px;
  min-height: 155px;

  display: flex;
  flex-flow: row;
  justify-content: flex-start;
  align-items: flex-start;

  @media (max-width: ${SCREEN_SM}) {
    width: 100%;
    padding: 0.24rem 0;
    min-height: 0;

    &:not(:last-child) {
      border-bottom: 1px solid #f0e4c8;
    }
  }
`;

/* Mirrors StyledBookCover */
const StyledCoverSkeleton = styled(SkeletonBlock)`
  width: 100px;
  min-height: 130px;
  border-radius: 2px;
  flex-shrink: 0;

  @media (max-width: ${SCREEN_SM}) {
    width: 1.7rem;
    min-height: 2.35rem;
  }
`;

/* Mirrors StyledBookRight */
const StyledRightSkeleton = styled.div`
  margin-left: 20px;

  display: flex;
  flex-flow: column;
  align-items: flex-start;

  @media (min-width: ${SCREEN_SM}) {
    width: 350px;
  }

  @media (max-width: ${SCREEN_SM}) {
    width: 100%;
    margin-left: 0.24rem;
    align-self: stretch;
  }
`;

/* Mirrors StyledBookNameDiv */
const StyledTitleSkeleton = styled(SkeletonBlock)`
  width: 62%;
  height: 26px;

  @media (max-width: ${SCREEN_SM}) {
    height: 0.4rem;
  }
`;

/* Mirrors StyledBookTag */
const StyledTagSkeleton = styled(SkeletonBlock)`
  width: 56px;
  height: 22px;
  margin-top: 8px;

  @media (max-width: ${SCREEN_SM}) {
    width: 1rem;
    height: 0.34rem;
    margin-top: 0.08rem;
  }
`;

/* Mirrors StyledBookMetaDiv */
const StyledMetaSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;

  @media (max-width: ${SCREEN_SM}) {
    margin-top: auto;
  }
`;

/* Mirrors StyledBookAuthorDiv */
const StyledAuthorSkeleton = styled(SkeletonBlock)`
  width: 30%;
  height: 20px;
  margin-top: 10px;

  @media (max-width: ${SCREEN_SM}) {
    height: 0.32rem;
    margin-top: 0.18rem;
  }
`;

/* Mirrors StyledRecommedTimeDiv */
const StyledDateSkeleton = styled(SkeletonBlock)`
  width: 52%;
  height: 20px;
  margin-top: 4px;

  @media (max-width: ${SCREEN_SM}) {
    height: 0.3rem;
    margin-top: 0.06rem;
  }
`;

/* Widths vary per row so the placeholder does not read as a hard grid. */
const TITLE_WIDTHS = ["58%", "70%", "48%", "64%", "54%", "66%"];
const CHIP_WIDTHS = [56, 76, 62, 62, 62, 62, 62, 62];

export interface BookListSkeletonProps {
  /** How many placeholder rows to draw. Defaults to 5. */
  rows?: number;
  /** Draw the tag filter bar placeholder above the list. Defaults to true. */
  showTagBar?: boolean;
  /** Draw the month / year-nav header placeholder. The year page has its own
      static header, so it passes false. Defaults to true. */
  showSectionHeader?: boolean;
}

export const BookListSkeleton: React.FC<BookListSkeletonProps> = ({
  rows = 5,
  showTagBar = true,
  showSectionHeader = true,
}) => {
  return (
    <React.Fragment>
      {showTagBar && (
        <StyledTagBarSkeleton aria-hidden="true">
          {CHIP_WIDTHS.map((w, i) => (
            <StyledChipSkeleton key={i} $w={w} />
          ))}
        </StyledTagBarSkeleton>
      )}

      {showSectionHeader && (
        <StyledSectionHeaderSkeleton aria-hidden="true">
          <StyledMonthSkeleton />
          <StyledNavSkeleton />
        </StyledSectionHeaderSkeleton>
      )}

      <StyledListSkeleton role="status" aria-label="书单加载中">
        {Array.from({ length: rows }).map((_, i) => (
          <StyledItemSkeleton key={i}>
            <StyledCoverSkeleton />
            <StyledRightSkeleton>
              <StyledTitleSkeleton
                style={{ width: TITLE_WIDTHS[i % TITLE_WIDTHS.length] }}
              />
              <StyledTagSkeleton />
              <StyledMetaSkeleton>
                <StyledAuthorSkeleton />
                <StyledDateSkeleton />
              </StyledMetaSkeleton>
            </StyledRightSkeleton>
          </StyledItemSkeleton>
        ))}
      </StyledListSkeleton>
    </React.Fragment>
  );
};

export default BookListSkeleton;
